/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Currencies Hooks - React Query for Currency Data
 * ============================================================================
 *
 * Custom hooks for currency data fetching and mutations using React Query.
 * Currencies are cached for 10 minutes as they rarely change.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { currenciesService, CurrencyDto, CreateCurrencyRequest } from '@/services/superadmin/currencies.service';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const currencyKeys = {
  all: ['currencies'] as const,
  active: () => [...currencyKeys.all, 'active'] as const,
  lists: () => [...currencyKeys.all, 'list'] as const,
  details: () => [...currencyKeys.all, 'detail'] as const,
  detail: (id: string) => [...currencyKeys.details(), id] as const,
  byCode: (code: string) => [...currencyKeys.all, 'code', code] as const,
};

/**
 * Hook to fetch all active currencies
 * Used for organization currency selection dropdowns
 */
export function useCurrencies(options?: { enabled?: boolean }) {
  return useQuery<CurrencyDto[], ApiError>({
    queryKey: currencyKeys.active(),
    queryFn: () => currenciesService.getActiveCurrencies(),
    staleTime: 10 * 60 * 1000, // 10 minutes - currencies rarely change
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch all currencies (including inactive)
 * Used for SuperAdmin currency management
 */
export function useAllCurrencies() {
  return useQuery<CurrencyDto[], ApiError>({
    queryKey: currencyKeys.lists(),
    queryFn: () => currenciesService.getAllCurrencies(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch currency by ID
 */
export function useCurrency(id: string) {
  return useQuery<CurrencyDto, ApiError>({
    queryKey: currencyKeys.detail(id),
    queryFn: () => currenciesService.getCurrencyById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch currency by code
 */
export function useCurrencyByCode(code: string) {
  return useQuery<CurrencyDto, ApiError>({
    queryKey: currencyKeys.byCode(code),
    queryFn: () => currenciesService.getCurrencyByCode(code),
    enabled: !!code,
  });
}

/**
 * Hook for creating a new currency (SuperAdmin only)
 */
export function useCreateCurrency() {
  const queryClient = useQueryClient();

  return useMutation<CurrencyDto, ApiError, CreateCurrencyRequest>({
    mutationFn: currenciesService.createCurrency,
    onSuccess: () => {
      // Invalidate both active and all lists
      queryClient.invalidateQueries({ queryKey: currencyKeys.active() });
      queryClient.invalidateQueries({ queryKey: currencyKeys.lists() });
    },
  });
}

/**
 * Hook for toggling currency active status (SuperAdmin only)
 */
export function useToggleCurrencyActive() {
  const queryClient = useQueryClient();

  return useMutation<CurrencyDto, ApiError, string>({
    mutationFn: currenciesService.toggleActive,
    onSuccess: (updatedCurrency, id) => {
      // Update the specific currency in cache
      queryClient.setQueryData(currencyKeys.detail(id), updatedCurrency);
      // Invalidate lists to reflect the status change
      queryClient.invalidateQueries({ queryKey: currencyKeys.active() });
      queryClient.invalidateQueries({ queryKey: currencyKeys.lists() });
    },
  });
}

/**
 * Hook for updating currency exchange rate (SuperAdmin only)
 */
export function useUpdateExchangeRate() {
  const queryClient = useQueryClient();

  return useMutation<CurrencyDto, ApiError, { id: string; exchangeRateToUsd: number }>({
    mutationFn: ({ id, exchangeRateToUsd }) =>
      currenciesService.updateExchangeRate(id, { exchangeRateToUsd }),
    onSuccess: (updatedCurrency, { id }) => {
      // Update the specific currency in cache
      queryClient.setQueryData(currencyKeys.detail(id), updatedCurrency);
      // Invalidate lists to reflect the rate change
      queryClient.invalidateQueries({ queryKey: currencyKeys.active() });
      queryClient.invalidateQueries({ queryKey: currencyKeys.lists() });
    },
  });
}

/**
 * Hook for deleting a currency (SuperAdmin only)
 */
export function useDeleteCurrency() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: currenciesService.deleteCurrency,
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: currencyKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: currencyKeys.active() });
      queryClient.invalidateQueries({ queryKey: currencyKeys.lists() });
    },
  });
}

/**
 * Helper hook to get currency options for dropdowns
 * Returns array of { value, label } for use in Select components
 */
export function useCurrencyOptions() {
  const { data: currencies = [], isLoading, error } = useCurrencies();

  const options = currencies.map((currency) => ({
    value: currency.code,
    label: `${currency.code} - ${currency.name} (${currency.symbol})`,
    currency, // Include full currency object for reference
  }));

  return { options, isLoading, error };
}
